import { useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../ui/button";
import { Eye, Hand, Ear, Wind, Coffee, X, Check, ArrowRight } from "lucide-react";

/**
 * 5-4-3-2-1 grounding technique — evidence-based anti-anxiety tool.
 * User identifies 5 things they see, 4 touch, 3 hear, 2 smell, 1 taste.
 */
const STEPS = [
  { key: "see",   count: 5, icon: Eye,    color: "#06b6d4" },
  { key: "touch", count: 4, icon: Hand,   color: "#8b5cf6" },
  { key: "hear",  count: 3, icon: Ear,    color: "#ec4899" },
  { key: "smell", count: 2, icon: Wind,   color: "#10b981" },
  { key: "taste", count: 1, icon: Coffee, color: "#f59e0b" },
];

const Grounding54321 = ({ onClose }) => {
  const { t } = useLanguage();
  const [stepIdx, setStepIdx] = useState(0);
  const [inputs, setInputs] = useState({});
  const [completed, setCompleted] = useState(false);

  const step = STEPS[stepIdx];
  const Icon = step?.icon;
  const slotsFilled = (inputs[step?.key] || []).filter(Boolean).length;
  const stepComplete = slotsFilled === step?.count;

  const setSlot = (i, value) => {
    const current = inputs[step.key] || [];
    const next = [...current]; next[i] = value;
    setInputs({ ...inputs, [step.key]: next });
  };

  const nextStep = () => {
    if (stepIdx === STEPS.length - 1) setCompleted(true);
    else setStepIdx(stepIdx + 1);
  };
  const restart = () => { setStepIdx(0); setInputs({}); setCompleted(false); };

  return (
    <div className="bg-[#0a0a0f] rounded-3xl p-8 border border-white/10" data-testid="grounding-tool">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-['Outfit'] text-2xl font-semibold mb-1">{t("tool_grounding_title")}</h2>
          <p className="text-white/50 text-sm">{t("tool_grounding_subtitle")}</p>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white p-2" data-testid="grounding-close-btn" aria-label="close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {!completed ? (
        <>
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6" data-testid="grounding-progress">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`h-1.5 rounded-full transition-all ${i < stepIdx ? "w-4 bg-white/60" : i === stepIdx ? "w-8" : "w-4 bg-white/10"}`} style={i === stepIdx ? { backgroundColor: s.color } : {}} />
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6 p-4 rounded-2xl" style={{ background: `${step.color}15`, border: `1px solid ${step.color}40` }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${step.color}30` }}>
              <Icon className="w-6 h-6" style={{ color: step.color }} />
            </div>
            <div className="flex-1">
              <p className="text-white font-['Outfit'] font-semibold text-lg">{step.count} {t(`tool_grounding_${step.key}_title`)}</p>
              <p className="text-white/60 text-sm">{t(`tool_grounding_${step.key}_hint`)}</p>
            </div>
          </div>

          <div className="space-y-2 mb-6">
            {Array.from({ length: step.count }).map((_, i) => {
              const value = (inputs[step.key] || [])[i] || "";
              return (
                <input
                  key={`${step.key}-${i}`}
                  type="text"
                  value={value}
                  onChange={(e) => setSlot(i, e.target.value)}
                  placeholder={`${i + 1}. ${t(`tool_grounding_${step.key}_placeholder`)}`}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
                  data-testid={`grounding-input-${step.key}-${i}`}
                />
              );
            })}
          </div>

          <div className="flex justify-end">
            <Button onClick={nextStep} disabled={!stepComplete} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 text-white rounded-full px-6 py-5 disabled:opacity-40" data-testid="grounding-next-btn">
              {stepIdx === STEPS.length - 1 ? t("tool_grounding_finish") : t("tool_grounding_next")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-10" data-testid="grounding-completed">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <Check className="w-8 h-8 text-emerald-300" />
          </div>
          <h3 className="font-['Outfit'] text-xl font-semibold mb-2">{t("tool_grounding_done_title")}</h3>
          <p className="text-white/60 text-sm mb-6 max-w-sm mx-auto">{t("tool_grounding_done_msg")}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={restart} variant="ghost" className="border border-white/20 text-white rounded-full px-6" data-testid="grounding-restart-btn">{t("tool_grounding_restart")}</Button>
            <Button onClick={onClose} className="bg-white text-black hover:bg-white/90 rounded-full px-6" data-testid="grounding-finish-close-btn">{t("tool_done")}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grounding54321;
